// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import ReactTable from 'react-table';
import { Form, TextArea } from 'informed';
import moment from 'moment';

import { Button, PrimaryButton, DeleteButton } from './Buttons.jsx';
import styles from '../styles/Table.scss';

type Props = {
  /* Each column must contain at least
  {
    Header: string,
    id: string,
  }

  Can optionally include
  cell: (row) => {}, to specify how to render the cell based on row, otherwise displays row.id
  editCell: (row) => {}, to specify how to render cell when in edit mode, else defaults to cell
  sortVal: (row) => {} to specify what value to sort the column based on, otherwise sorts on row.id

  Can also include any other attributes for React Table columns, will be passed through
  */
  columns: Object[],
  data: Object[],
  /*
     (rowId: string) => {}
     if not provided, delete button will not appear for entire table
  */
  handleDeleteRow?: Function,
  /*
    (row: Object) => boolean
    used for row level control of whether to show delete button
  */
  showDelete?: Function,
  /*
    Optional text displayed in delete button confirmation modal
  */
  deleteText?: string,
  /*
    (rowId: string, row: Object) => {}
    if not provided, edit button will not appear for entire table
  */
  handleSaveRow?: Function,
  /*
    (row: Object) => boolean
    used for row level control of whether to show edit button
  */
  showEdit?: Function,
  /*
    (row: Object) => {}
    if not provided, add button will not appear
  */
  handleSaveNewRow?: Function,

  /*
    Optional extra button/component to add in actions column.
    Takes row object, returns a React node
  */
  extraActions?: Object => React.Node,
  /*
    Width of actions column if necessary. Otherwise 120 by default for Edit and X buttons
  */
  actionsWidth?: number,
  /*
    Omits expandable notes from table
    (used now just for interventions table)
  */
  omitNotes?: boolean,
  // activeAccount passed in for edit history
  activeAccount: Object,
  // print passed in to change styles when printing
  print: boolean,
  // Any other props will be passed into ReactTable to override defaults
}

type State = {
  editRowId: string,
  addingNewRow: boolean,
  expanded: Object,
}

const NEW_ROW_ID = 'newRow';

/*
  Wrapper around react-table to give default styles.
  Hides pagination and instead makes the table scrollable with all results
  Not sustainable if results too numerous (would want to reintroduce pagination),
  but should be ok for EHR use.
*/
class TableComponent extends React.Component<Props, State> {
  formApi: {
    getState: Function,
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      editRowId: '',
      addingNewRow: false,
      expanded: {},
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.print && !prevProps.print) {
      const expanded = {};
      for (let i = 0; i < this.props.data.length; i++) {
        if (this.props.data[i].notes) {
          expanded[i] = true;
        }
      }
      this.setState({
        expanded,
      });
    } else if (!this.props.print && prevProps.print) {
      const expanded = {};
      for (let i = 0; i < this.props.data.length; i++) {
        expanded[i] = false;
      }
      this.setState({
        expanded,
      });
    }
  }

  handleEditRow = (row: Object) => {
    this.setState({
      editRowId: row.original._id,
      expanded: {
        [row.index]: true,
      },
    });
  }

  handleCancelEdit = (row: Object) => {
    this.setState({
      editRowId: '',
      addingNewRow: false,
      expanded: {
        [row.index]: false,
      },
    });
  }

  handleSaveRow = (row: Object) => {
    const state = this.formApi.getState();
    if (state.invalid) {
      return;
    }
    // Add active account to all requests for edit history
    const { activeAccount } = this.props;
    const values = {
      ...state.values,
      editor: `${activeAccount.name}, ${activeAccount.credential}`,
      editDate: moment().toISOString(),
    };

    // Only send request if at least one field was touched
    if (Object.keys(state.touched).length && this.props.handleSaveRow) {
      this.props.handleSaveRow(this.state.editRowId, {
        ...state,
        values,
      }, row.original); // row.original sent as extra parameter, usually not needed/used
    }

    this.setState({
      editRowId: '',
      expanded: {
        [row.index]: false,
      },
    });
  }

  handleAddRow = () => {
    this.setState({
      editRowId: NEW_ROW_ID,
      addingNewRow: true,
      expanded: {
        /* eslint-disable quote-props */
        '0': true,
      },
    });
  }

  handleSaveNewRow = (row: Object) => {
    const state = this.formApi.getState();
    if (state.invalid) {
      return;
    }
    // Add active account to all requests for edit history
    const { activeAccount } = this.props;
    const values = {
      ...state.values,
      editor: `${activeAccount.name}, ${activeAccount.credential}`,
      editDate: moment().toISOString(),
    };

    if (this.props.handleSaveNewRow) {
      this.props.handleSaveNewRow({
        ...state,
        values,
      }, row.original); // row.original sent as extra parameter, usually not needed/used
    }

    this.setState({
      editRowId: '',
      addingNewRow: false,
      expanded: {
        [row.index]: false,
      },
    });
  }

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
  }

  actionsAccessor = (row: Object) => {
    const defaultButtons = (
      <div>
        {this.props.handleSaveRow && (!this.props.showEdit || this.props.showEdit(row.original))
          && <PrimaryButton onClick={() => this.handleEditRow(row)}>Edit</PrimaryButton>
        }
        {this.props.handleDeleteRow && (!this.props.showDelete || this.props.showDelete(row.original))
          && <DeleteButton
            onClick={() => {
              if (this.props.handleDeleteRow) {
                // row.original sent as extra parameter, usually not needed/used
                this.props.handleDeleteRow(row.original._id, row.original);
              }
            }}
            modalText={this.props.deleteText}
          >
            X
          </DeleteButton>}
        {this.props.extraActions && this.props.extraActions(row.original)}
      </div>
    );

    const editButtons = (
      <div>
        <PrimaryButton onClick={this.state.editRowId === NEW_ROW_ID
          ? (() => this.handleSaveNewRow(row)) : (() => this.handleSaveRow(row))}>
          Save
        </PrimaryButton>
        <Button onClick={() => this.handleCancelEdit(row)}>Cancel</Button>
      </div>
    );
    return row.original._id === this.state.editRowId ? editButtons : defaultButtons;
  }

  notesComponent = (row) => {
    let editHistory = '';
    if (!this.state.addingNewRow && row.original.editors) {
      for (let i = 0; i < row.original.editors.length; i++) {
        // This outer if statement is ad hoc solution to not show null editors/dates input into DB from bug (now fixed)
        if (row.original.editors[i]) {
          if (i === row.original.editors.length - 1) {
            editHistory += `${moment(row.original.editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${row.original.editors[i]} (created) \n`;
          } else {
            editHistory += `${moment(row.original.editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${row.original.editors[i]}\n`;
          }
        }
      }
    }
    return <div className={styles.notesExpansion}>
      <strong>Notes</strong> <br />
      {this.state.editRowId === row.original._id
        ? <TextArea field='notes' initialValue={row.original.notes} />
        : row.original.notes || '--'
      }
      {!this.props.print
        && <div>
          <strong>Edit History</strong> <br />
          {editHistory || '--'}
        </div>
      }
    </div>;
  }

  render() {
    const {
      data, columns, handleDeleteRow, handleSaveRow, handleSaveNewRow, extraActions, print, ...rest
    } = this.props;

    const reactTableCols = this.props.columns.map((column) => {
      const {
        Header, id, sortVal, cell, editCell, ...colRest
      } = column;

      return {
        Header,
        id,
        // if sortVal function specified use, else use attr value itself
        accessor: row => (sortVal ? sortVal(row) : row[id]),
        Cell: ({ original }) => {
          const row = original;

          // If current row is being editted, and cell has editCell attr, show editCell
          if (row._id === this.state.editRowId && editCell) {
            return editCell(row);
          }

          // if cell render is specified use it, else use attr value itself
          return cell ? cell(row) : row[id] || '--';
        },
        ...colRest,
      };
    });

    const actionsColumn = {
      Header: 'Actions',
      id: 'actions',
      Cell: this.actionsAccessor,
      width: this.props.actionsWidth || 120,
    };

    const expanderColumn = {
      Header: 'Notes',
      expander: true,
      width: 50,
      Expander: ({ original, isExpanded }) => <div>
        {isExpanded
          ? <span className={`${original.notes ? styles.notes : styles.noNotes}`}>&#x2299;</span>
          : <span className={`${original.notes ? styles.notes : styles.noNotes}`}>&#x2295;</span>}
      </div>,
      style: {
        cursor: 'pointer',
        fontSize: 25,
        padding: '0',
        textAlign: 'center',
        userSelect: 'none',
      },
      headerStyle: {
        display: 'inline',
      },
    };

    if (!this.props.omitNotes && !print) {
      reactTableCols.push(expanderColumn);
    }

    if ((this.props.handleSaveRow || this.props.handleDeleteRow || this.props.extraActions) && !print) {
      reactTableCols.push(actionsColumn);
    }

    const tableData = this.state.addingNewRow ? [{ _id: NEW_ROW_ID }, ...data] : data;

    if (!tableData.length && print) {
      return 'No data';
    }

    return (
      <Form getApi={this.setFormApi} autoComplete='off'>
        {(!print && this.props.handleSaveNewRow)
          && <PrimaryButton onClick={this.handleAddRow}>Add Row</PrimaryButton>}
        <ReactTable
          columns={reactTableCols}
          data={tableData}
          className="-striped -highlight"
          style={{
            maxHeight: `${print ? '' : '400px'}`,
            width: '100%',
          }}
          showPagination={false}
          // if no results, table height 3 to display no rows text
          pageSize={this.props.data.length
            ? this.props.data.length + (this.state.addingNewRow ? 1 : 0) : 2}
          expanded={this.state.expanded}
          onExpandedChange={expanded => this.setState({ expanded })}
          SubComponent={this.props.omitNotes ? null : this.notesComponent}
          NoDataComponent={() => <div className={styles.noDataComponent}>No rows found</div>}
          {...rest}
        />
      </Form>
    );
  }
}

const mapStateToProps = state => ({
  activeAccount: state.account.activeAccount,
  print: state.patient.print,
});

const Table = connect(
  mapStateToProps,
)(TableComponent);

export default Table;



// WEBPACK FOOTER //
// ./client/src/components/Table.jsx